/**
 * The browser's `expo/fetch`: the global fetch, same origin, with the cookie jar.
 *
 * The phone names `expo/fetch` so the request survives React Native's own
 * fetch being swapped in; the browser has one fetch and it already streams.
 *
 * One difference is kept on purpose. `expo/fetch` rejects a request that never
 * reached a server with a `FetchError`, and the phone's failure classifier
 * (`apps/mobile/src/lib/sync-failure.ts`) reads that name as "unreachable"
 * while it reads a bare `TypeError` as a bug. The browser rejects the same
 * failure with a `TypeError`, so it is renamed here and the classifier holds
 * for both.
 *
 * Every call ends here, so here is where the browser says who it is:
 * `x-alluzo-client: web/<version>/<features>` (web-redesign-02 phase 1). The
 * phone's `@/lib/client-fetch` runs above this and names the phone; this
 * replaces that value, because in a browser the caller is the web app.
 */
import { clientHeaderValue, withClientHeader, type ClientFeature } from '@alluzo/shared';

import { version } from '../../package.json';

/**
 * What the web app understands. `roles`: it draws Roles, a person's access and
 * Reports to (web-redesign-03), and OS's role and access cards (web-redesign-08
 * phase 7), so OS offers it the role tools and kinds rather than the old jobs
 * and levels. `set_view`: the record page applies a view OS sets
 * (os-live-mode.md 3.1). `fill_entry`: the new-entry page takes what OS fills
 * into it (web-redesign-10 phase 7). `team_cards`: it draws OS's phase-3 team
 * cards (roles-parity 3.7), so OS offers it the tools that make them.
 * `doc_templates` and `business_details`: it draws report templates and the
 * business detail card (report-templates.md D24), which the phone never sees.
 * `connectors`: it has the Connectors pages and draws OS's email and rule
 * cards (docs/plans/connectors-gmail.md Decided 17), so OS offers it the
 * Gmail tools.
 */
const WEB_FEATURES: readonly ClientFeature[] = [
  'roles',
  'set_view',
  'fill_entry',
  'team_cards',
  'doc_templates',
  'business_details',
  'connectors',
];

export const WEB_CLIENT = clientHeaderValue('web', version, WEB_FEATURES);

class FetchError extends Error {
  override name = 'FetchError';
}

export const fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  try {
    const sent: RequestInit = { credentials: 'same-origin', ...init };
    return await globalThis.fetch(input, withClientHeader(sent, WEB_CLIENT));
  } catch (error) {
    if (error instanceof TypeError) throw new FetchError(error.message, { cause: error });
    throw error;
  }
};
